import { Stack, Typography, ToggleButton, Divider, RadioGroup, useTheme } from '@mui/material';
import { Radio, ToggleSelect } from '@perry-weather/component-library';
import {
  ABBREVIATIONS,
  Granularity,
  OBSERVATIONS,
  OBSERVATIONS_DATA_INTERVAL,
  OBSERVATIONS_DATA_INTERVAL_ARRAY,
} from '../../../Constants/HistoricalReportConstants';

interface WeatherObservationsProps {
  isAllSelected: boolean;
  selectedDataInterval: string;
  isPrecipValid: boolean;
  showPrecipError: boolean;
  isDateRangeValid: boolean;
  selectedGranularity: Granularity;
  observationSwitchAllTrigger: boolean[];
  styles: {
    chip: any;
  };
  handleSelectAllObservations: () => void;
  handleSelectedObservations: (observation: string, metrics: string[]) => void;
  handleObservationDataIntervalChange: (newDataInterval: string) => void;
}

const WeatherObservations = (props: WeatherObservationsProps) => {
  const {
    isAllSelected,
    selectedDataInterval,
    isPrecipValid,
    showPrecipError,
    isDateRangeValid,
    selectedGranularity,
    observationSwitchAllTrigger,
    styles,
    handleSelectAllObservations,
    handleSelectedObservations,
    handleObservationDataIntervalChange,
  } = props;

  const theme = useTheme();

  return (
    <>
      <Typography variant='h6' fontWeight='bold'>
        Weather Observations
      </Typography>
      <Typography color='textSecondary' overflow='visible'>
        Select the weather variables you'd like to include in your report
      </Typography>

      <ToggleButton
        selected={isAllSelected}
        value={''}
        onChange={handleSelectAllObservations}
        sx={{ ...styles.chip, margin: '14px 0' }}>
        Select All
      </ToggleButton>

      <Stack direction='row' flexWrap='wrap' gap='14px'>
        {OBSERVATIONS.map(item => {
          const invalidInterval = OBSERVATIONS_DATA_INTERVAL[item.minInterval] > selectedGranularity;

          const isErrored = item.value === 'Precipitation' && !isPrecipValid;

          return (
            <ToggleSelect
              header='Options'
              key={item.value}
              value={item.value}
              title={item.label}
              menuHeightAdjustment={2}
              selectAll={!invalidInterval && observationSwitchAllTrigger[0]}
              deselectAll={invalidInterval || observationSwitchAllTrigger[1]}
              options={item.metrics}
              abbreviations={ABBREVIATIONS}
              tooltip={
                invalidInterval ? `This data is only available for a ${item.minInterval} data interval.` : undefined
              }
              disabled={invalidInterval}
              menuWidth={118}
              sx={{
                text: { fontWeight: 'bold' },
                button: isErrored
                  ? {
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: theme.palette.error.main,
                      },
                    }
                  : {},
              }}
              onChange={handleSelectedObservations}
            />
          );
        })}
      </Stack>

      {showPrecipError && (
        <Typography color={theme.palette.error.light} overflow='visible' noWrap sx={{ marginTop: '16px' }}>
          Precipitation is limited to 24-hour data intervals for legacy weather stations. Newer models capture up to
          15-minute intervals.
        </Typography>
      )}

      <Divider style={{ width: '100%', marginTop: '24px', marginBottom: '14px' }} />

      <Typography variant='h6' fontWeight='bold'>
        Data Interval
      </Typography>
      <Typography color='textSecondary' overflow='visible'>
        Choose how often the data is collected.
      </Typography>

      <RadioGroup
        row
        value={selectedDataInterval}
        name='radio-data-interval'
        sx={{ gap: '18px' }}
        onChange={(_, value) => handleObservationDataIntervalChange(value)}>
        {OBSERVATIONS_DATA_INTERVAL_ARRAY.map(([interval]) => (
          <Radio key={interval} value={interval} label={interval} size='small' />
        ))}
      </RadioGroup>

      {!isDateRangeValid && (
        <Typography color={theme.palette.error.light} overflow='visible' noWrap>
          Update your date range to use this interval.
        </Typography>
      )}
    </>
  );
};

export default WeatherObservations;
